/**
 * Validation Service
 * 
 * Provides async validation utilities for dynamic form fields.
 * Handles API-dependent validations with caching support.
 */

import dayjs from 'dayjs';
import { getIn } from 'formik';
import { parseIntOrZero } from '../../../util/util';

/** End date tolerance: allow up to 4 weeks (28 days) after the nominal end date */
const END_DATE_TOLERANCE_DAYS = 28;

/**
 * API Response Cache
 * Stores validation results to avoid duplicate API calls
 */
const validationCache = new Map();

/**
 * Get cache key for API calls
 * @param {Object} context - Validation context
 * @param {Object} fieldConfig - Field configuration
 * @param {string} prefix - Optional prefix for cache key (e.g., 'crf')
 */
const getCacheKey = (context, fieldConfig, prefix = 'validation') => {
  const { sponsorId, trialId, siteId, subjectMasterId, crfMasterId } = context;
  const {
    dependStepKey,
    dependSectionKey,
    dependSubSectionKey,
    dependCategoryKey,
    dependFieldKey,
  } = fieldConfig || {};
  
  const baseKey = `${prefix}_${sponsorId}_${trialId}_${siteId}_${subjectMasterId || ''}_${crfMasterId || ''}_${dependStepKey}_${dependSectionKey}_${dependSubSectionKey}_${dependCategoryKey}_${dependFieldKey}`;
  return baseKey;
};

const parseDateValue = (value) => {
  if (!value) return null;

  const ddmmyyyy = dayjs(value, 'DD/MM/YYYY');
  if (ddmmyyyy.isValid()) return ddmmyyyy;

  const yyyymmdd = dayjs(value, 'YYYY-MM-DD');
  if (yyyymmdd.isValid()) return yyyymmdd;

  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

const getYearBasedDiagnosisDate = (yearOfDiagnosis, indexDate) => {
  const year = parseInt(yearOfDiagnosis, 10);
  if (isNaN(year) || year < 1900 || year > 2100) {
    return null;
  }

  if (!indexDate) {
    return dayjs(`${year}-07-01`, 'YYYY-MM-DD');
  }

  const index = parseDateValue(indexDate);
  if (!index) return null;

  const indexYear = index.year();
  if (year > indexYear) return null;

  if (year < indexYear) {
    return dayjs(`${year}-07-01`, 'YYYY-MM-DD');
  }

  const julyFirstCurrentYear = dayjs(`${indexYear}-07-01`, 'YYYY-MM-DD');
  const janFirstCurrentYear = dayjs(`${indexYear}-01-01`, 'YYYY-MM-DD');
  const isOnOrAfterJulyFirst =
    index.isAfter(julyFirstCurrentYear, 'day') || index.isSame(julyFirstCurrentYear, 'day');
  return isOnOrAfterJulyFirst ? julyFirstCurrentYear : janFirstCurrentYear;
};

export const getDiagnosisDateInfoFromValues = ({ dateOfDiagnosis, yearOfDiagnosis, indexDate }) => {
  const date = parseDateValue(dateOfDiagnosis);
  if (date) {
    return {
      date,
      source: 'dateOfDiagnosis',
      originalValue: dateOfDiagnosis,
    };
  }

  const yearBasedDate = getYearBasedDiagnosisDate(yearOfDiagnosis, indexDate);
  if (yearBasedDate) {
    return {
      date: yearBasedDate,
      source: 'yearOfDiagnosis',
      originalValue: yearOfDiagnosis,
    };
  }

  return null;
};

export const getDiagnosisDateInfo = (answers, categoryKey, indexEventDate) => {
  if (!answers || !categoryKey) {
    return null;
  }

  const category = answers[categoryKey];
  if (!category) {
    return null;
  }

  return getDiagnosisDateInfoFromValues({
    dateOfDiagnosis: category['dateOfDiagnosis'],
    yearOfDiagnosis: category['yearOfDiagnosis'],
    indexDate: indexEventDate,
  });
};

/**
 * Get diagnosis date from answers, falling back to yearOfDiagnosis if dateOfDiagnosis is not available
 * If yearOfDiagnosis is used, returns the mid date of that year (July 1st)
 *
 * @param {Object} answers - Subject answers object
 * @param {string} categoryKey - Category key to look for diagnosis data
 * @returns {Object|null} - { date: dayjs object, source: 'dateOfDiagnosis' | 'yearOfDiagnosis', originalValue: string }
 */
export const getDiagnosisDate = (answers, categoryKey) => {
  if (!answers || !categoryKey) {
    return null;
  }

  const category = answers[categoryKey];
  if (!category) {
    return null;
  }

  return getDiagnosisDateInfoFromValues({
    dateOfDiagnosis: category['dateOfDiagnosis'],
    yearOfDiagnosis: category['yearOfDiagnosis'],
    indexDate: null,
  });
};

export const calculateDiagnosisDurationYears = ({
  dateOfDiagnosis,
  yearOfDiagnosis,
  indexDate,
  diagnosisDateInfo,
}) => {
  const diagnosisInfo =
    diagnosisDateInfo || getDiagnosisDateInfoFromValues({ dateOfDiagnosis, yearOfDiagnosis, indexDate });
  if (!diagnosisInfo?.date) return null;

  const index = parseDateValue(indexDate);
  if (!index) return null;

  const duration = index.diff(diagnosisInfo.date, 'year', true);
  const roundedDuration = Math.round(duration * 10) / 10;
  return roundedDuration < 0 ? 0 : roundedDuration;
};

export const calculateDiagnosisDurationMonths = ({
  dateOfDiagnosis,
  yearOfDiagnosis,
  indexDate,
  diagnosisDateInfo,
}) => {
  const diagnosisInfo =
    diagnosisDateInfo || getDiagnosisDateInfoFromValues({ dateOfDiagnosis, yearOfDiagnosis, indexDate });
  if (!diagnosisInfo?.date) return null;

  const index = parseDateValue(indexDate);
  if (!index) return null;

  return index.diff(diagnosisInfo.date, 'month', true);
};

/**
 * Calculate if diagnosis is at least 6 months before index date
 * Uses conservative logic for year-only diagnosis dates
 * 
 * For year-only dates:
 * - If index date >= July 1st: Exclude same year (ambiguous), include previous year or earlier
 * - If index date < July 1st: Exclude same year AND previous year (both ambiguous), include 2+ years ago
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.diagnosisDateInfo - Diagnosis date info from getDiagnosisDateInfo
 * @param {string} params.indexDate - Index date string (DD/MM/YYYY format)
 * @returns {boolean|null} - true if >= 6 months, false if < 6 months, null if calculation not possible
 */
export const calculateIsAtLeast6MonthsConservative = ({
  diagnosisDateInfo,
  indexDate,
}) => {
  if (!diagnosisDateInfo?.date) return null;

  const index = parseDateValue(indexDate);
  if (!index || !index.isValid()) return null;

  // If exact date available, use raw duration in years (no rounding) so that >= 6 months (0.5 years) is correctly identified
  // e.g. 0.5001 years (just over 6 months) must return true (eligible); avoids rounding edge cases with smaller decimals
  if (diagnosisDateInfo.source === 'dateOfDiagnosis') {
    const durationYears = index.diff(diagnosisDateInfo.date, 'year', true);
    return durationYears >= 0.5;
  }

  // If only year available, use conservative logic
  const diagnosisYear = diagnosisDateInfo.date.year();
  const indexYear = index.year();
  const indexMonth = index.month(); // 0-11, where 6 = July

  if (indexMonth >= 6) {
    // Index date >= July 1st
    // Exclude same year (ambiguous - could be < 6 months)
    if (diagnosisYear === indexYear) {
      return false;
    }
    // Include previous year or earlier (definitely >= 6 months)
    return diagnosisYear < indexYear;
  } else {
    // Index date < July 1st
    // Exclude same year and previous year (both ambiguous)
    if (diagnosisYear === indexYear || diagnosisYear === (indexYear - 1)) {
      return false;
    }
    // Include only if 2+ years earlier (definitely >= 6 months)
    return diagnosisYear < (indexYear - 1);
  }
};

/**
 * Calculate if diagnosis duration is within 8 years (≤ 8 years)
 * Uses conservative logic for year-only diagnosis dates
 * 
 * For year-only dates:
 * - Exclude (return false) if indexYear - diagnosisYear >= 8 (could be > 8 years)
 * - Include (return true) if indexYear - diagnosisYear < 8 (definitely ≤ 8 years)
 * 
 * Examples:
 * - Index 2020, diagnosis 2011: diff=9 >= 8 → false (exclude)
 * - Index 2020, diagnosis 2012: diff=8 >= 8 → false (exclude)
 * - Index 2020, diagnosis 2013: diff=7 < 8 → true (include)
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.diagnosisDateInfo - Diagnosis date info from getDiagnosisDateInfo
 * @param {string} params.indexDate - Index date string (DD/MM/YYYY format)
 * @returns {boolean|null} - true if ≤ 8 years, false if > 8 years, null if calculation not possible
 */
export const calculateIsWithin8YearsConservative = ({
  diagnosisDateInfo,
  indexDate,
}) => {
  if (!diagnosisDateInfo?.date) return null;

  const index = parseDateValue(indexDate);
  if (!index || !index.isValid()) return null;

  if (diagnosisDateInfo.source === 'dateOfDiagnosis') {
    const durationYears = index.diff(diagnosisDateInfo.date, 'year', true);
    return durationYears <= 8;
  }

  // If only year available, use conservative logic
  const diagnosisYear = diagnosisDateInfo.date.year();
  const indexYear = index.year();
  const yearDiff = indexYear - diagnosisYear;

  // Exclude if year difference >= 8 (could be > 8 years)
  // Include if year difference < 8 (definitely ≤ 8 years)
  return yearDiff < 8;
};

/**
 * Fetch subject answers with caching
 */
export const getSubjectAnswersCached = async (context, fieldConfig, getSubjectAnswers, skipCache = false) => {
  if (!getSubjectAnswers) {
    throw new Error('getSubjectAnswers function is required');
  }

  const { sponsorId, trialId, siteId, subjectMasterId } = context;
  const {
    dependStepKey,
    dependSectionKey,
    dependSubSectionKey,
    dependCategoryKey,
    dependFieldKey,
  } = fieldConfig || {};

  if (!siteId) {
    return null;
  }

  const cacheKey = getCacheKey(context, fieldConfig);

  // Check cache first
  if (validationCache.has(cacheKey) && !skipCache) {
    return validationCache.get(cacheKey);
  }

  try {
    const result = await getSubjectAnswers({
      sponsorId,
      trialId,
      siteId,
      payload: {
        subjectMasterId,
        stepKey: dependStepKey,
        sectionKey: dependSectionKey,
        subSectionKey: dependSubSectionKey,
        categoryKey: dependCategoryKey,
        fieldKey: dependFieldKey,
      },
    }).unwrap();

    // Cache the result
    if (!skipCache) {
      validationCache.set(cacheKey, result);
    }
    return result;
  } catch (error) {
    // console.error('[validationService] Error fetching subject answers:', error);
    throw error;
  }
};

/**
 * Fetch CRF answers with caching
 */
const getCRFAnswersCached = async (context, fieldConfig, getCRFAnswers, skipCache = false) => {
  if (!getCRFAnswers) {
    throw new Error('getCRFAnswers function is required');
  }

  const { sponsorId, trialId, siteId, crfMasterId, subjectMasterId } = context;
  const {
    dependStepKey,
    dependSectionKey,
    dependSubSectionKey,
    dependCategoryKey,
    dependFieldKey,
  } = fieldConfig || {};

  if (!siteId) {
    return null;
  }

  const cacheKey = getCacheKey(context, fieldConfig, 'crf'); // Optional: Use a different cache prefix for CRF

  // Check cache first
  if (validationCache.has(cacheKey) && !skipCache) {
    return validationCache.get(cacheKey);
  }

  try {
    const result = await getCRFAnswers({
      sponsorId,
      trialId,
      siteId,
      payload: {
        crfMasterId,
        subjectMasterId,
        stepKey: dependStepKey,
        sectionKey: dependSectionKey || null,
        subSectionKey: dependSubSectionKey || null,
        categoryKey: dependCategoryKey || null,
        fieldKey: dependFieldKey|| undefined,
      },
    }).unwrap();

    // Cache the result
    if (!skipCache) {
      validationCache.set(cacheKey, result);
    }
    return result;
  } catch (error) {
    // console.error('[validationService] Error fetching CRF answers:', error);
    throw error;
  }
};



/**
 * Clear validation cache for specific context
 */
export const clearValidationCache = (context) => {
  if (!context) {
    validationCache.clear();
    return;
  }

  const { sponsorId, trialId, siteId, subjectMasterId } = context;
  const prefix = `validation_${sponsorId}_${trialId}_${siteId}_${subjectMasterId}_`;
  
  for (const key of validationCache.keys()) {
    if (key.startsWith(prefix)) {
      validationCache.delete(key);
    }
  }
};

/**
 * Validate date of diagnosis
 * Must be 6 months to 8 years before index date
 */
export const validateDateOfDiagnosis = async (value, fieldConfig, formValues) => {
  
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const indexDatePath = fieldConfig?.dependCategoryKey && fieldConfig?.dependFieldKey
      ? `${fieldConfig.dependCategoryKey}.${fieldConfig.dependFieldKey}`
      : null;


    if (!indexDatePath) {
      const errorMessage = 'Index date configuration missing';
      // console.error('[validationService] validateDateOfDiagnosis: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    const indexDateValue = getIn(formValues, indexDatePath);

    if (!indexDateValue) {
      return true; // If index date not available, skip validation
    }

    const diagnosisDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexDateValue, 'DD/MM/YYYY');

    if (!diagnosisDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateOfDiagnosis: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    const isAfterIndexDate = diagnosisDate.isAfter(indexDate);
    if (isAfterIndexDate) {
      const errorMessage = 'Date of diagnosis must be before index date';
      return { isValid: false, message: errorMessage };
    }

    // const diffInMonths = indexDate.diff(diagnosisDate, 'month', true);
    // const diffInYears = indexDate.diff(diagnosisDate, 'year', true);

    // const lessThanSixMonths = diffInMonths < 6;
    // const moreThanEightYears = diffInYears > 8;

    // if (lessThanSixMonths || moreThanEightYears) {
    //   const errorMessage = 'Patient must be diagnosed at least 6 months and not more than 8 years before index date';
    //   // console.error('[validationService] validateDateOfDiagnosis: Validation failed -', errorMessage, {
    //   //   lessThanSixMonths,
    //   //   moreThanEightYears
    //   // });
    //   return { isValid: false, message: errorMessage };
    // }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateOfDiagnosis: Exception -', error);
    const errorMessage = 'Unable to validate date of diagnosis';
    return { isValid: false, message: errorMessage };
  }
};

export const validateDateOfEnrolment = async (value, fieldConfig, formValues) => {

  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const indexDatePath =
      fieldConfig?.dependCategoryKey && fieldConfig?.dependFieldKey
        ? `${fieldConfig.dependCategoryKey}.${fieldConfig.dependFieldKey}`
        : null;

    if (!indexDatePath) {
      return;
    }

    const indexEventDate = getIn(formValues, indexDatePath);

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    // Parse both dates as dayjs objects
    const enrolmentDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!enrolmentDate.isValid()) {
      const errorMessage = 'Invalid date of enrolment format';
      return { isValid: false, message: errorMessage };
    }

    if (!indexDate.isValid()) {
      const errorMessage = 'Invalid index date format';
      return { isValid: false, message: errorMessage };
    }

    // Check if date of enrolment is not after index date
    if (!enrolmentDate.isAfter(indexDate)) {
      const errorMessage = `Date of enrolment (${enrolmentDate.format('DD/MM/YYYY')}) must be after index date (${indexDate.format('DD/MM/YYYY')})`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date of enrolment';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate year of diagnosis
 * The selected year cannot be more than index date year
 */
export const validateYearOfDiagnosis = async (value, fieldConfig, formValues) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const indexDatePath = fieldConfig?.dependCategoryKey && fieldConfig?.dependFieldKey
    ? `${fieldConfig.dependCategoryKey}.${fieldConfig.dependFieldKey}`
    : null;


  if (!indexDatePath) {
    const errorMessage = 'Index date configuration missing';
    // console.error('[validationService] validateDateOfDiagnosis: Error -', errorMessage);
    return { isValid: false, message: errorMessage };
  }

  const indexEventDate = getIn(formValues, indexDatePath);

  if (!indexEventDate) {
    return true; // If index date not available, skip validation
  }

    // Parse the selected year
    const selectedYear = parseInt(value, 10);
    if (isNaN(selectedYear)) {
      const errorMessage = 'Invalid year format';
      // console.error('[validationService] validateYearOfDiagnosis: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Parse index date to get the year
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');
    if (!indexDate.isValid()) {
      const errorMessage = 'Invalid index date format';
      // console.error('[validationService] validateYearOfDiagnosis: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    const indexYear = indexDate.year();

    // Check if selected year is greater than index date year
    if (selectedYear > indexYear) {
      const errorMessage = `Year of diagnosis (${selectedYear}) cannot be after index date year (${indexYear})`;
      // console.error('[validationService] validateYearOfDiagnosis: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateYearOfDiagnosis: Exception -', error);
    const errorMessage = 'Unable to validate year of diagnosis';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate most recent CD characteristics date
 * Must be between diagnosis date and just before index date
 */
export const validateMostRecentDate = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, {...fieldConfig, dependFieldKey: ""}, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
    const diagnosisDateInfo = getDiagnosisDate(answers, fieldConfig?.dependCategoryKey);

    if (!diagnosisDateInfo) {
      return true; // If diagnosis date not available, skip validation
    }

    const indexEventDateDate = dayjs(indexEventDate, 'DD/MM/YYYY');
    const mostRecentDate = dayjs(value, 'DD/MM/YYYY');

    if (!indexEventDateDate.isValid() || !mostRecentDate.isValid()) {
      const errorMessage = 'Unable to validate most recent CD characteristics date';
      return { isValid: false, message: errorMessage };
    }

    // Check if only yearOfDiagnosis is available (no exact date)
    if (diagnosisDateInfo.source === 'yearOfDiagnosis') {
      // Year-based validation: year must be >= yearOfDiagnosis AND < indexYear (or same year but before index date)
      const diagnosisYear = parseInt(diagnosisDateInfo.originalValue, 10);
      const indexYear = indexEventDateDate.year();
      const inputYear = mostRecentDate.year();

      if (isNaN(diagnosisYear)) {
        return true; // If diagnosis year is invalid, skip validation
      }

      const isBeforeDiagnosisYear = inputYear < diagnosisYear;
      // For "before index date" - allow same year as index (since exact date comparison handles the rest)
      // but if input year > index year, that's definitely invalid
      const isAfterIndexYear = inputYear > indexYear;

      if (isBeforeDiagnosisYear || isAfterIndexYear) {
        const errorMessage = `Most recent CD characteristics date year must be between Diagnosis Year (${diagnosisYear}) and Index Year (${indexYear}). Date must also be before Index Date.`;
        return { isValid: false, message: errorMessage };
      }

      // If same year as index, still check that it's before the index date
      if (inputYear === indexYear && !mostRecentDate.isBefore(indexEventDateDate)) {
        const errorMessage = `Most recent CD characteristics date must be before Index Date (${indexEventDateDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    } else {
      // Exact date validation
      const dateOfDiagnosisDate = diagnosisDateInfo.date;

      if (!dateOfDiagnosisDate.isValid()) {
        const errorMessage = 'Unable to validate most recent CD characteristics date';
        return { isValid: false, message: errorMessage };
      }

      const isBeforeDiagnosis = mostRecentDate.isBefore(dateOfDiagnosisDate);
      const isOnOrAfterIndex = !mostRecentDate.isBefore(indexEventDateDate); // means >= index

      if (isBeforeDiagnosis || isOnOrAfterIndex) {
        const errorMessage = `Most recent CD characteristics date must be between Diagnosis Date (${dateOfDiagnosisDate.format('DD/MM/YYYY')}) and just before Index Date (${indexEventDateDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate most recent CD characteristics date';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate 5-ASA start date
 * Must be between (Index date – 2 years) and index date
 */
export const validate5AsaStartDate = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, {...fieldConfig, dependFieldKey: ""}, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];
    const dateOfDiagnosis = answers?.[fieldConfig?.dependCategoryKey]?.['dateOfDiagnosis'];
    const yearOfDiagnosis = answers?.[fieldConfig?.dependCategoryKey]?.['yearOfDiagnosis'];

    const asaStartDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!asaStartDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Check if exact dateOfDiagnosis is available
    const hasExactDiagnosisDate = dateOfDiagnosis && dateOfDiagnosis !== '';
    const hasYearOnly = !hasExactDiagnosisDate && yearOfDiagnosis && yearOfDiagnosis !== '';

    if (hasExactDiagnosisDate) {
      // Use exact date validation: start date must be between dateOfDiagnosis and indexDate
      const diagnosisDate = dayjs(dateOfDiagnosis, 'DD/MM/YYYY');
      
      if (!diagnosisDate.isValid()) {
        return true; // If diagnosis date is invalid, skip validation
      }

      const isBeforeDiagnosis = asaStartDate.isBefore(diagnosisDate, 'day');
      const isAfterIndex = asaStartDate.isAfter(indexDate, 'day');

      if (isBeforeDiagnosis || isAfterIndex) {
        const errorMessage = `Date must be between Diagnosis Date (${diagnosisDate.format('DD/MM/YYYY')}) and Index Date (${indexDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    } else if (hasYearOnly) {
      // Use year-only validation: start date year must be between yearOfDiagnosis and indexDate year
      const diagnosisYear = parseInt(yearOfDiagnosis, 10);
      const indexYear = indexDate.year();
      const startDateYear = asaStartDate.year();

      if (isNaN(diagnosisYear)) {
        return true; // If diagnosis year is invalid, skip validation
      }

      const isBeforeDiagnosisYear = startDateYear < diagnosisYear;
      const isAfterIndexYear = startDateYear > indexYear;

      if (isBeforeDiagnosisYear || isAfterIndexYear) {
        const errorMessage = `Date year must be between Diagnosis Year (${diagnosisYear}) and Index Year (${indexYear})`;
        return { isValid: false, message: errorMessage };
      }

      // If same year as index, still check that it's not after the index date
      if (startDateYear === indexYear && asaStartDate.isAfter(indexDate, 'day')) {
        const errorMessage = `Date must not be after Index Date (${indexDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    } else {
      // No diagnosis date available, skip validation
      return true;
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate 5-ASA start date';
    return { isValid: false, message: errorMessage };
  }
};

export const validate5AEndDate = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }
  
  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];
    
    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const asaEndDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!asaEndDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validate5AEndDate: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Calculate (Index date – 2 years)
    const minDateFromIndex = indexDate.subtract(2, 'year');
    const maxDate = indexDate;

    // Get start date value if startDateKey is configured
    let startDate = null;
    if (fieldConfig?.startDateKey) {
      const startDateFieldPath = fieldConfig.startDateKey;
      const startDateFieldKey = startDateFieldPath.includes('.') ? startDateFieldPath.split('.') : startDateFieldPath;
      const startDateValue = startDateFieldPath.includes('.')
        ? formValues[startDateFieldKey[0]]?.[startDateFieldKey[1]]
        : formValues[startDateFieldKey];
      
      if (startDateValue) {
        startDate = dayjs(startDateValue, 'DD/MM/YYYY');
        if (!startDate.isValid()) {
          const errorMessage = 'Invalid start date format';
          // console.error('[validationService] validate5AEndDate: Error -', errorMessage);
          return { isValid: false, message: errorMessage };
        }
      }
    }

    // Validation 1: End date should not be smaller than start date
    if (startDate && asaEndDate.isBefore(startDate, 'day')) {
      const errorMessage = 'End date must not be smaller than start date';
      // console.error('[validationService] validate5AEndDate: Validation failed -', errorMessage, {
      //   endDate: asaEndDate.format('DD/MM/YYYY'),
      //   startDate: startDate.format('DD/MM/YYYY'),
      // });
      return { isValid: false, message: errorMessage };
    }

    // Validation 2: End date should not be smaller than (Index date – 2 years)
    if (asaEndDate.isBefore(minDateFromIndex, 'day')) {
      const errorMessage = `End date must not be smaller than (Index date – 2 years). Minimum allowed date is ${minDateFromIndex.format('DD/MM/YYYY')}`;
      // console.error('[validationService] validate5AEndDate: Validation failed -', errorMessage, {
      //   endDate: asaEndDate.format('DD/MM/YYYY'),
      //   minDateFromIndex: minDateFromIndex.format('DD/MM/YYYY'),
      //   indexDate: indexDate.format('DD/MM/YYYY'),
      // });
      return { isValid: false, message: errorMessage };
    }

    // Validation 3: End date should not be after index date
    if (asaEndDate.isAfter(maxDate, 'day')) {
      const errorMessage = `End date must not be after index date. Maximum allowed date is ${maxDate.format('DD/MM/YYYY')}`;
      // console.error('[validationService] validate5AEndDate: Validation failed -', errorMessage, {
      //   endDate: asaEndDate.format('DD/MM/YYYY'),
      //   maxDate: maxDate.format('DD/MM/YYYY'),
      // });
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validate5AEndDate: Exception -', error);
    const errorMessage = 'Unable to validate 5-ASA end date';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date range: (Index date – 1 month) to index date
 * Date must be between (Index date – 1 month) and index date (inclusive)
 */
export const validateDateRangeIndexMinus1Month = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateRangeIndexMinus1Month: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Calculate (Index date – 1 month)
    const minDate = indexDate.subtract(1, 'month');
    const maxDate = indexDate;

    // Check if date is before (Index date – 1 month)
    const isBeforeMinDate = inputDate.isBefore(minDate, 'day');
    // Check if date is after index date
    const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

    if (isBeforeMinDate) {
      const errorMessage = `Date must not be before (Index date – 1 month). Earliest allowed: ${minDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }
    if (isAfterMaxDate) {
      const errorMessage = `Date must not be after index date. Latest allowed: ${maxDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

export const validateDateRangeAfterIndex = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateRangeAfterIndex: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Get start date if startDateKey is available
    let startDate = null;
    if (fieldConfig?.startDateKey) {
      let startDatePath = fieldConfig.startDateKey;

      // Handle dynamic index for repeated rows
      if (fieldConfig.key && fieldConfig.key.includes('-')) {
        const indexKeys = fieldConfig.key.split('-');
        const indexKeyIndex = indexKeys[indexKeys.length - 1];
        startDatePath = startDatePath + '-' + indexKeyIndex;
      }

      const startDateKey = startDatePath.includes('.') ? startDatePath.split('.') : startDatePath;
      const startDateValue = startDatePath.includes('.')
        ? formValues[startDateKey[0]]?.[startDateKey[1]]
        : formValues[startDateKey];
      
      if (startDateValue) {
        startDate = dayjs(startDateValue, 'DD/MM/YYYY');
        if (!startDate.isValid()) {
          const errorMessage = 'Invalid start date format';
          // console.error('[validationService] validateDateRangeAfterIndex: Error -', errorMessage);
          return { isValid: false, message: errorMessage };
        }
      }
    }

    // Validation 1: Check if input date is before start date (if startDateKey is available)
    if (startDate && inputDate.isBefore(startDate, 'day')) {
      const errorMessage = `Date must not be before start date (${startDate.format('DD/MM/YYYY')}). Earliest allowed: ${startDate.format('DD/MM/YYYY')}`;
      // console.error('[validationService] validateDateRangeAfterIndex: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Validation 2: Check if input date is after or on index date
    if (inputDate.isBefore(indexDate, 'day')) {
      const errorMessage = `Date must be on or after index date (${indexDate.format('DD/MM/YYYY')}).`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateRangeAfterIndex: Exception -', error);
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date range: Before or on index date
 * Date must be before or equal to the index date
 */
export const validateDateRangeBeforeIndex = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Validation: Check if input date is after index date
    if (inputDate.isAfter(indexDate, 'day')) {
      const errorMessage = `Date must be on or before index date (${indexDate.format('DD/MM/YYYY')}).`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate stop date: Must be on or after both index date and start date
 * Stop date should not be before the index date AND should not be before the start date
 */
export const validateStopDateAfterIndexAndStartDate = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = indexEventDate ? dayjs(indexEventDate, 'DD/MM/YYYY') : null;

    if (!inputDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Get start date if startDateKey is available
    let startDate = null;
    if (fieldConfig?.startDateKey) {
      let startDatePath = fieldConfig.startDateKey;

      // Handle dynamic index for repeated rows
      if (fieldConfig.key && fieldConfig.key.includes('-')) {
        const indexKeys = fieldConfig.key.split('-');
        const indexKeyIndex = indexKeys[indexKeys.length - 1];
        startDatePath = startDatePath + '-' + indexKeyIndex;
      }

      const startDateKey = startDatePath.includes('.') ? startDatePath.split('.') : startDatePath;
      const startDateValue = startDatePath.includes('.')
        ? formValues[startDateKey[0]]?.[startDateKey[1]]
        : formValues[startDateKey];
      
      if (startDateValue) {
        startDate = dayjs(startDateValue, 'DD/MM/YYYY');
        if (!startDate.isValid()) {
          const errorMessage = 'Invalid start date format';
          return { isValid: false, message: errorMessage };
        }
      }
    }

    // Validation 1: Check if stop date is after index date (must be on or before index date)
    if (indexDate && indexDate.isValid() && inputDate.isAfter(indexDate, 'day')) {
      const errorMessage = `Stop date (${inputDate.format('DD/MM/YYYY')}) must be on or before index date (${indexDate.format('DD/MM/YYYY')}).`;
      return { isValid: false, message: errorMessage };
    }

    // Validation 2: Check if stop date is on or before start date (must be after start date)
    if (startDate && !inputDate.isAfter(startDate, 'day')) {
      const errorMessage = `Stop date (${inputDate.format('DD/MM/YYYY')}) must be after start date (${startDate.format('DD/MM/YYYY')}).`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate stop date';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date range: After index date till 28 February 2024
 * Date must be after index date and before or equal to 28 February 2024
 */
export const validateDateAfterIndexTillFeb2024 = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');
    const feb2024 = dayjs('28/02/2024', 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid() || !feb2024.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateAfterIndexTillFeb2024: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Check if date is on or before index date
    const isOnOrBeforeIndexDate = !inputDate.isAfter(indexDate, 'day');
    // Check if date is after 28 February 2024
    const isAfterFeb2024 = inputDate.isAfter(feb2024, 'day');

    if (isOnOrBeforeIndexDate) {
      const errorMessage = `Date must be after index date (${indexDate.format('DD/MM/YYYY')}).`;
      return { isValid: false, message: errorMessage };
    }
    if (isAfterFeb2024) {
      const errorMessage = `Date must be before or equal to 28 February 2024. Selected date: ${inputDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }


    return true;
  } catch (error) {
    // console.error('[validationService] validateDateAfterIndexTillFeb2024: Exception -', error);
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate age criteria
 * Age must match selected criteria (Yes/No)
 */
export const validateAgeCriteria = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);
    
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const ageValue = answers?.[fieldConfig?.dependCategoryKey]?.[fieldConfig?.dependFieldKey];
    const age = parseIntOrZero(ageValue);

    if (value === 'Yes' && age < 18) {
      const errorMessage = 'Age at index event is < 18 years, but Inclusion Criterion 2 is selected as Yes. Please check';
      // console.error('[validationService] validateAgeCriteria: Validation failed -', errorMessage, { value, age });
      return { isValid: false, message: errorMessage };
    }

    if (value === 'No' && age >= 18) {
      const errorMessage = 'Age at index event is >= 18 years, but Inclusion Criterion 2 is selected as No. Please check';
      // console.error('[validationService] validateAgeCriteria: Validation failed -', errorMessage, { value, age });
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateAgeCriteria: Exception -', error);
    const errorMessage = 'Unable to validate age criterion';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate diagnosed with CD in 6 months
 * Handles both exact dateOfDiagnosis and yearOfDiagnosis (with conservative logic for year-only)
 */
export const validateDiagnosedWithCDin6months = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const isDiagnososDateAvailable = answers?.[fieldConfig?.dependCategoryKey]?.['dateOfDiagnosisAvailable'];
    
    if (isDiagnososDateAvailable === 'Unknown' && value === 'Yes') {
      const errorMessage = 'Date of diagnosis is unknown, but Inclusion Criterion 3 is selected as Yes';
      // console.error('[validationService] validateDiagnosedWithCDin6months: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');
    if (!indexDate.isValid()) {
      return true; // If index date is invalid, skip validation
    }

    // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
    const diagnosisDateInfo = getDiagnosisDateInfo(answers, fieldConfig?.dependCategoryKey, indexEventDate);

    if (!diagnosisDateInfo) {
      return true; // If diagnosis date not available, skip validation
    }

    // Use shared conservative calculation for 6-month check
    const atLeast6Months = calculateIsAtLeast6MonthsConservative({
      diagnosisDateInfo,
      indexDate: indexEventDate,
    });

    if (atLeast6Months === null) {
      return true; // If calculation not possible, skip validation
    }

    if (value === 'Yes' && !atLeast6Months) {
      const diagnosisDisplay = diagnosisDateInfo.source === 'yearOfDiagnosis'
        ? `year ${diagnosisDateInfo.originalValue} (estimated)`
        : diagnosisDateInfo.date.format('DD/MM/YYYY');
      const errorMessage = `Duration of diagnosis is <6 months (diagnosis: ${diagnosisDisplay}, index: ${indexDate.format('DD/MM/YYYY')}), but Inclusion Criterion 3 is selected as Yes.`;
      // console.error('[validationService] validateDiagnosedWithCDin6months: Validation failed -', errorMessage, { 
      //   value, 
      //   diagnosisSource: diagnosisDateInfo.source,
      //   atLeast6Months 
      // });
      return { isValid: false, message: errorMessage };
    }

    if (value === 'No' && atLeast6Months) {
      const diagnosisDisplay = diagnosisDateInfo.source === 'yearOfDiagnosis'
        ? `year ${diagnosisDateInfo.originalValue} (estimated)`
        : diagnosisDateInfo.date.format('DD/MM/YYYY');
      const errorMessage = `Duration of diagnosis is >=6 months (diagnosis: ${diagnosisDisplay}, index: ${indexDate.format('DD/MM/YYYY')}), but Inclusion Criterion 3 is selected as No.`;
      // console.error('[validationService] validateDiagnosedWithCDin6months: Validation failed -', errorMessage, { 
      //   value, 
      //   diagnosisSource: diagnosisDateInfo.source,
      //   atLeast6Months 
      // });
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDiagnosedWithCDin6months: Exception -', error);
    const errorMessage = 'Unable to validate diagnosed with CD in 6 months';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate prior exposure
 * Based on cdst_advanced_therapies_used_before_index value:
 * - If '2 or more', should be 'Yes'
 * - Otherwise, should be 'No'
 */
export const validatePriorExposure = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const cdstAdvancedTherapiesUsedBeforeIndex = 
      answers?.[fieldConfig?.dependCategoryKey]?.['cdst_advanced_therapies_used_before_index'];

    // If cdst_advanced_therapies_used_before_index is '2 or more', value should be 'Yes'
    if (cdstAdvancedTherapiesUsedBeforeIndex === '2 or more' && value === 'No') {
      const errorMessage = 'Number of advanced therapies used before index is 2 or more, but Exclusion Criterion 2 is selected as No. Please check.';
      // console.error('[validationService] validatePriorExposure: Validation failed -', errorMessage, { value, cdstAdvancedTherapiesUsedBeforeIndex });
      return { isValid: false, message: errorMessage };
    }

    // If cdst_advanced_therapies_used_before_index is NOT '2 or more', value should be 'No'
    if (cdstAdvancedTherapiesUsedBeforeIndex !== '2 or more' && value === 'Yes') {
      const errorMessage = 'Number of advanced therapies used before index is less than 2, but Exclusion Criterion 2 is selected as Yes. Please check.';
      // console.error('[validationService] validatePriorExposure: Validation failed -', errorMessage, { value, cdstAdvancedTherapiesUsedBeforeIndex });
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validatePriorExposure: Exception -', error);
    const errorMessage = 'Unable to validate prior exposure';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate age at index date
 * Age at index date should not be greater than participant's age
 */
export const validateAgeAtIndexDate = async (value, fieldConfig, formValues) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const ageAtIndexDate = parseFloat(value);
    if (isNaN(ageAtIndexDate)) {
      return this?.createError?.({
        message: 'Invalid age value'
      }) || false;
    }

    const ageValue = formValues?.[fieldConfig?.dependCategoryKey]?.[fieldConfig?.dependFieldKey];

    if(ageValue === undefined || ageValue === null || ageValue === '') {
      return true;
    }

    const age = parseIntOrZero(ageValue);

    if (ageAtIndexDate > age) {
      return this?.createError?.({
        message: 'Age at Index Date should not be greater than age at date of enrolment. Please check'
      }) || false;
    }

    return true;
  } catch (error) {
    // console.error('[validationService] Error validating age at index date:', error);
    return this?.createError?.({
      message: 'Unable to validate age at index date'
    }) || false;
  }
};

/**
 * Generic async validation wrapper with error handling
 */
export const validateWithErrorHandling = async (validationFn, errorMessage = 'Validation failed') => {
  try {
    return await validationFn();
  } catch (error) {
    // console.error('[validationService] Validation error:', error);
    return this?.createError?.({ message: errorMessage }) || false;
  }
};

/**
 * Validate eligibility confirmation (inclusion/exclusion criteria)
 */
export const validateEligibilityConfirmation = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const validation = fieldConfig?.validation?.[0];
    if (!validation?.inclusionCriteria || !validation?.exclusionCriteria) {
      return true; // Skip if config not available
    }

    // Get inclusion criteria
    // const inclusionCacheKey = `getSubjectAnswers_${context.sponsorId}_${context.trialId}_${context.siteId}_${context.subjectMasterId}_${validation.inclusionCriteria.dependStepKey}_${validation.inclusionCriteria.dependSectionKey}_${validation.inclusionCriteria.dependSubSectionKey}_${validation.inclusionCriteria.dependCategoryKey}_${validation.inclusionCriteria.dependFieldKey}`;
    
    // let inclusionAnswers;
    // if (validationCache.has(inclusionCacheKey)) {
    //   inclusionAnswers = validationCache.get(inclusionCacheKey);
    // } else {
     const inclusionAnswers = await getSubjectAnswers({
        sponsorId: context.sponsorId,
        trialId: context.trialId,
        siteId: context.siteId,
        payload: {
          subjectMasterId: context.subjectMasterId,
          stepKey: validation.inclusionCriteria.dependStepKey,
          sectionKey: validation.inclusionCriteria.dependSectionKey,
          subSectionKey: validation.inclusionCriteria.dependSubSectionKey,
          categoryKey: validation.inclusionCriteria.dependCategoryKey,
          fieldKey: validation.inclusionCriteria.dependFieldKey,
        },
      }).unwrap();;
    //   validationCache.set(inclusionCacheKey, inclusionAnswers);
    // }

    // Get exclusion criteria
    // const exclusionCacheKey = `getSubjectAnswers_${context.sponsorId}_${context.trialId}_${context.siteId}_${context.subjectMasterId}_${validation.exclusionCriteria.dependStepKey}_${validation.exclusionCriteria.dependSectionKey}_${validation.exclusionCriteria.dependSubSectionKey}_${validation.exclusionCriteria.dependCategoryKey}_${validation.exclusionCriteria.dependFieldKey}`;
    
    // let exclusionAnswers;
    // if (validationCache.has(exclusionCacheKey)) {
    //   exclusionAnswers = validationCache.get(exclusionCacheKey);
    // } else {
     const exclusionAnswers = await getSubjectAnswers({
        sponsorId: context.sponsorId,
        trialId: context.trialId,
        siteId: context.siteId,
        payload: {
          subjectMasterId: context.subjectMasterId,
          stepKey: validation.exclusionCriteria.dependStepKey,
          sectionKey: validation.exclusionCriteria.dependSectionKey,
          subSectionKey: validation.exclusionCriteria.dependSubSectionKey,
          categoryKey: validation.exclusionCriteria.dependCategoryKey,
          fieldKey: validation.exclusionCriteria.dependFieldKey,
        },
      }).unwrap();;
    //   validationCache.set(exclusionCacheKey, exclusionAnswers);
    // }

    const inclusionCriteria = inclusionAnswers?.[validation.inclusionCriteria.dependCategoryKey]?.[validation.inclusionCriteria.dependFieldKey];
    const exclusionCriteria = exclusionAnswers?.[validation.exclusionCriteria.dependCategoryKey]?.[validation.exclusionCriteria.dependFieldKey];

    if (inclusionCriteria === undefined || exclusionCriteria === undefined || inclusionCriteria === null || exclusionCriteria === null) {
      const errorMessage = 'Data is not available for inclusion or exclusion criteria. Please complete previous sections.';
      // console.error('[validationService] validateEligibilityConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (value === 'Yes' && inclusionCriteria === 'No' && exclusionCriteria === 'Yes') {
      const errorMessage = 'Patient is indicated to be Eligible for Study, but Inclusion Criteria met = No and Exclusion Criteria Present = Yes.';
      // console.error('[validationService] validateEligibilityConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (value === 'Yes' && inclusionCriteria === 'No') {
      const errorMessage = 'Patient is indicated to be Eligible for Study, but Inclusion Criteria met = No.';
      // console.error('[validationService] validateEligibilityConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (value === 'Yes' && exclusionCriteria === 'Yes') {
      const errorMessage = 'Patient is indicated to be Eligible for Study, but Exclusion Criteria Present = Yes.';
      // console.error('[validationService] validateEligibilityConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (value === 'No' && inclusionCriteria === 'Yes' && exclusionCriteria === 'No') {
      const errorMessage = 'Patient is indicated to not be Eligible for Study, but Inclusion Criteria met = Yes and Exclusion Criteria Present = No';
      // console.error('[validationService] validateEligibilityConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateEligibilityConfirmation: Exception -', error);
    const errorMessage = 'Unable to validate eligibility confirmation.';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate severity HBI (Harvey-Bradshaw Index)
 */
export const validateSeverityHbi = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const ans =
      answers?.[fieldConfig?.dependCategoryKey]?.[
        'HBI_at_baseline_individual_scores_available__Yes__HBI_at_initiation_of_VDZ_therapy__HBI_SCORE'
      ] ||
      answers?.[fieldConfig?.dependCategoryKey]?.[
        'HBI_at_baseline_individual_scores_available__No__HBI_score_at_initiation_of_VDZ_therapy'
      ];
    const hbiScore = parseIntOrZero(ans);

    if (value === 'Yes' && hbiScore < 8) {
      const errorMessage = 'HBI is < 8, but Inclusion Criterion 5 is indicated as Yes';
      // console.error('[validationService] validateSeverityHbi: Validation failed -', errorMessage, { value, hbiScore });
      return { isValid: false, message: errorMessage };
    }

    if (value === 'No' && hbiScore >= 8) {
      const errorMessage = 'HBI is >= 8, but Inclusion Criterion 5 is indicated as No';
      // console.error('[validationService] validateSeverityHbi: Validation failed -', errorMessage, { value, hbiScore });
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateSeverityHbi: Exception -', error);
    const errorMessage = 'Unable to validate HBI severity';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate HBI assessment confirmation
 */
export const validateHbiAssessmentConfirmation = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    // Special cache key for this validation (no categoryKey/fieldKey in payload)
    const cacheKey = `getSubjectAnswers_${context.sponsorId}_${context.trialId}_${context.siteId}_${context.subjectMasterId}_${fieldConfig?.dependStepKey}_${fieldConfig?.dependSectionKey}_${fieldConfig?.dependSubSectionKey}__`;
    
    let answers;
    if (validationCache.has(cacheKey)) {
      answers = validationCache.get(cacheKey);
    } else {
      answers = await getSubjectAnswers({
        sponsorId: context.sponsorId,
        trialId: context.trialId,
        siteId: context.siteId,
        payload: {
          subjectMasterId: context.subjectMasterId,
          stepKey: fieldConfig?.dependStepKey,
          sectionKey: fieldConfig?.dependSectionKey,
          subSectionKey: fieldConfig?.dependSubSectionKey,
          categoryKey: '',
          fieldKey: '',
        },
      });
      validationCache.set(cacheKey, answers);
    }

    const baselineHBI = answers?.HBI_at_baseline_pdl?.HBI_at_baseline_available;
    const hbiScoreAtWeek26 = answers?.HBI_at_week_26_pdl?.HBI_at_week_26_available;

    if (value === 'Yes' && baselineHBI === 'No') {
      const errorMessage = 'Baseline HBI is not available, but Inclusion Criterion 7 is indicated as Yes';
      // console.error('[validationService] validateHbiAssessmentConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (value === 'Yes' && hbiScoreAtWeek26 === 'No') {
      const errorMessage = 'Inclusion Criterion 7 is indicated as Yes, but Week 26 HBI not completed and no VDZ discontinuation date before week 26.';
      // console.error('[validationService] validateHbiAssessmentConfirmation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateHbiAssessmentConfirmation: Exception -', error);
    const errorMessage = 'Unable to validate HBI assessment confirmation';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate patients with no CDST score
 */
export const validatePatientsWithNoCDSTScore = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const historyOfBowelSurgery = answers?.[fieldConfig?.dependCategoryKey]?.cdst_history_of_bowel_surgery;
    const historyOfPriorTNFAntagonistTherapy = answers?.[fieldConfig?.dependCategoryKey]?.['cdst_history_of_prior_TNF-antagonist_the'];
    const historyOfPriorFistulizingDisease = answers?.[fieldConfig?.dependCategoryKey]?.['cdst_history_of_prior_fistulizing_disease'];
    const cdstBaselineAlbuminAvailable = answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_albumin_available;
    const cdstBaselineCRPAvailable = answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_crp_available;
    const baselineAlbumin = answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_albumin;
    const baselineCRP = answers?.[fieldConfig?.dependCategoryKey]?.cdst_baseline_crp;

    const hasUnknownCategorical =
      historyOfBowelSurgery === 'Unknown' ||
      historyOfPriorTNFAntagonistTherapy === 'Unknown' ||
      historyOfPriorFistulizingDisease === 'Unknown';

    const albuminMissing =
      cdstBaselineAlbuminAvailable !== 'Yes' ||
      baselineAlbumin === null ||
      baselineAlbumin === undefined ||
      baselineAlbumin === '';

    const crpMissing =
      cdstBaselineCRPAvailable !== 'Yes' || baselineCRP === null || baselineCRP === undefined || baselineCRP === '';

    const cdstNotCalculable = hasUnknownCategorical || albuminMissing || crpMissing;
    
    if (value === 'Yes' && !cdstNotCalculable) {
      const errorMessage =
        'Exclusion Criterion 3 indicated as Yes, but all CDST predictor variables are available (CDST can be calculated).';
      // console.error('[validationService] validatePatientsWithNoCDSTScore: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (
      value === 'No' && cdstNotCalculable
    ) {
      const errorMessage = 'Exclusion Criterion 3 indicated as No, but at least one CDST predictor variable is not available/unknown (CDST cannot be calculated).';
      // console.error('[validationService] validatePatientsWithNoCDSTScore: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validatePatientsWithNoCDSTScore: Exception -', error);
    const errorMessage = 'Unable to validate CDST score';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate duration of disease
 * Calculates duration from diagnosis date (or mid date if only year available) to index date
 */
export const validateDurationDisease = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);
    
    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];
    const isDiagnososDateAvailable = answers?.[fieldConfig?.dependCategoryKey]?.['dateOfDiagnosisAvailable'];

    if (isDiagnososDateAvailable === 'Unknown' && value === 'Yes') {
      const errorMessage = 'Date of diagnosis is unknown, but Inclusion Criterion 4 is selected as Yes';
      return { isValid: false, message: errorMessage };
    }

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
    const diagnosisDateInfo = getDiagnosisDateInfo(answers, fieldConfig?.dependCategoryKey, indexEventDate);

    if (!diagnosisDateInfo) {
      return true; // If diagnosis date not available, skip validation
    }

    // Use conservative calculation for year-only dates
    // For exact dates: uses normal ≤ 8 year calculation
    // For year-only: excludes if indexYear - diagnosisYear >= 8
    const isWithin8Years = calculateIsWithin8YearsConservative({
      diagnosisDateInfo,
      indexDate: indexEventDate,
    });
    
    if (isWithin8Years === null) {
      return true; // If calculation not possible, skip validation
    }

    if (value === 'Yes' && !isWithin8Years) {
      const indexParsed = parseDateValue(indexEventDate);
      const indexYear = indexParsed?.year() || 'N/A';
      const diagnosisDisplay = diagnosisDateInfo.source === 'yearOfDiagnosis'
        ? `year ${diagnosisDateInfo.originalValue}`
        : diagnosisDateInfo.date.format('DD/MM/YYYY');
      const errorMessage = `Duration of diagnosis is >=8 years or ambiguous (diagnosis: ${diagnosisDisplay}, index year: ${indexYear}), but Inclusion Criterion 4 is selected as Yes.`;
      return { isValid: false, message: errorMessage };
    }

    if (value === 'No' && isWithin8Years) {
      const indexParsed = parseDateValue(indexEventDate);
      const indexYear = indexParsed?.year() || 'N/A';
      const diagnosisDisplay = diagnosisDateInfo.source === 'yearOfDiagnosis'
        ? `year ${diagnosisDateInfo.originalValue}`
        : diagnosisDateInfo.date.format('DD/MM/YYYY');
      const errorMessage = `Duration of diagnosis is <8 years (diagnosis: ${diagnosisDisplay}, index year: ${indexYear}), but Inclusion Criterion 4 is selected as No.`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate duration of disease';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate index date between January 2016 and February 2024
 */
export const validateIndexDateBetweenJan2016AndFeb2024 = async (value, fieldConfig, formValues, context, getSubjectAnswers) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {

    // if (value === 'No') {
    //   return { isValid: false, message: "The index date must be between Jan 2016 and 28 Feb 2024 for subject eligibility." };
    // }

    return true;
  } catch (error) {
    // console.error('[validationService] validateIndexDateBetweenJan2016AndFeb2024: Exception -', error);
    const errorMessage = 'Unable to validate index date.';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date range: Date of Diagnosis to (date of diagnosis + 6 months)
 * Date must be between date of diagnosis and 6 months after date of diagnosis (inclusive)
 */
export const validateDateRangeDiagnosisToDiagnosisPlus6Months = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, {...fieldConfig, dependFieldKey: ""}, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
    const diagnosisDateInfo = getDiagnosisDate(answers, fieldConfig?.dependCategoryKey);

    if (!diagnosisDateInfo) {
      return true; // If diagnosis date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');

    if (!inputDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Check if only yearOfDiagnosis is available (no exact date)
    if (diagnosisDateInfo.source === 'yearOfDiagnosis') {
      // Year-based validation: year must be >= yearOfDiagnosis AND <= (yearOfDiagnosis + 1)
      // (+1 because diagnosis + 6 months could cross into next year)
      const diagnosisYear = parseInt(diagnosisDateInfo.originalValue, 10);
      const inputYear = inputDate.year();
      const maxYear = diagnosisYear + 1; // 6 months from any point in the year could extend to next year

      if (isNaN(diagnosisYear)) {
        return true; // If diagnosis year is invalid, skip validation
      }

      const isBeforeDiagnosisYear = inputYear < diagnosisYear;
      const isAfterMaxYear = inputYear > maxYear;

      if (isBeforeDiagnosisYear || isAfterMaxYear) {
        const errorMessage = `Date year must be between Diagnosis Year (${diagnosisYear}) and ${maxYear} (diagnosis + 6 months range)`;
        return { isValid: false, message: errorMessage };
      }
    } else {
      // Exact date validation
      const diagnosisDate = diagnosisDateInfo.date;

      if (!diagnosisDate.isValid()) {
        const errorMessage = 'Invalid date format';
        return { isValid: false, message: errorMessage };
      }

      // Calculate (date of diagnosis + 6 months)
      const minDate = diagnosisDate;
      const maxDate = diagnosisDate.add(6, 'month');

      // Check if date is before date of diagnosis
      const isBeforeMinDate = inputDate.isBefore(minDate, 'day');
      // Check if date is after (date of diagnosis + 6 months)
      const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

      if (isBeforeMinDate) {
        const errorMessage = `Date must not be before date of diagnosis (${minDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
      if (isAfterMaxDate) {
        const errorMessage = `Date must not be after (date of diagnosis + 6 months). Last allowed: ${maxDate.format('DD/MM/YYYY')} (diagnosis date: ${diagnosisDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date range: (Index date – 6 months) to index date
 * Date must be between (Index date – 6 months) and index date (inclusive)
 */
export const validateDateRangeIndexMinus6Months = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateRangeIndexMinus6Months: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Calculate (Index date – 6 months)
    const minDate = indexDate.subtract(6, 'month');
    const maxDate = indexDate;

    // Check if date is before (Index date – 6 months)
    const isBeforeMinDate = inputDate.isBefore(minDate, 'day');
    // Check if date is after index date
    const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

    if (isBeforeMinDate) {
      const errorMessage = `Date must not be before (Index date – 6 months). Earliest allowed: ${minDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }
    if (isAfterMaxDate) {
      const errorMessage = `Date must not be after index date. Latest allowed: ${maxDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateRangeIndexMinus6Months: Exception -', error);
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date is within ±1 week (±7 days) from index date
 * 
 * Use case: Fields that should contain dates approximately at the index date
 * (within a 1-week tolerance window before or after)
 * 
 * @param {string} value - Date value in DD/MM/YYYY format
 * @param {Object} fieldConfig - Field configuration containing dependCategoryKey
 * @param {Object} formValues - All form values
 * @param {Object} context - Validation context
 * @param {Function} getSubjectAnswers - Function to fetch subject answers
 * @returns {boolean|Object} - true if valid, or { isValid: false, message: string } if invalid
 */
export const validateDateWithin1WeekOfIndex = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  try {
    const indexEventDate = formValues?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!value || !indexEventDate) {
      return true;
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Default tolerance is ±7 days (1 week), but can be overridden in fieldConfig
    const toleranceDays = fieldConfig?.toleranceDays ?? 7;

    // Calculate the difference in days from index date
    const diffInDays = Math.abs(inputDate.diff(indexDate, 'day'));

    if (diffInDays > toleranceDays) {
      const minValidDate = indexDate.subtract(toleranceDays, 'day');
      const maxValidDate = indexDate.add(toleranceDays, 'day');
      
      const errorMessage = `Date must be within ±${toleranceDays} days of index date. Index date: ${indexDate.format('DD/MM/YYYY')}. Valid range: ${minValidDate.format('DD/MM/YYYY')} to ${maxValidDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date within 1 week of index date';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validates that a date is within ±6 months of the index date
 * 
 * @param {string} value - The date value to validate (DD/MM/YYYY format)
 * @param {Object} fieldConfig - Field configuration containing dependCategoryKey and optional toleranceMonths
 * @param {Object} formValues - Current form values
 * @param {Object} context - Validation context
 * @param {Function} getSubjectAnswers - Function to fetch subject answers
 * @returns {boolean|Object} - true if valid, or object with isValid: false and message
 */
export const validateDateWithin6MonthsOfIndex = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  try {
    const indexEventDate = formValues?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!value || !indexEventDate) {
      return true;
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Default tolerance is ±6 months, but can be overridden in fieldConfig
    const toleranceMonths = fieldConfig?.toleranceMonths ?? 6;

    // Calculate the valid date range
    const minValidDate = indexDate.subtract(toleranceMonths, 'month');
    // const maxValidDate = indexDate.add(toleranceMonths, 'month');

    // Check if the input date is within the valid range
    if (inputDate.isBefore(minValidDate, 'day') || inputDate.isAfter(indexDate, 'day')) {
      const errorMessage = `Date must be within ${toleranceMonths} months of index date. Index date: ${indexDate.format('DD/MM/YYYY')}. Valid range: ${minValidDate.format('DD/MM/YYYY')} to ${indexDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date within 6 months of index date';
    return { isValid: false, message: errorMessage };
  }
};

export const validateDateAfter26WeeksIndex = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {

  try {
    const indexEventDate = formValues?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!value || !indexEventDate) {
      return true;
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateAfter26WeeksIndex: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    const targetWeeks = fieldConfig?.targetWeeks || [26];
    const toleranceDays = fieldConfig?.toleranceDays || 7; // Default ±7 days tolerance

    // Check if date is close to any of the target weeks
    let isValidWeek = false;
    let matchedWeek = null;

    for (const week of targetWeeks) {
      const targetDate = indexDate.add(week, 'week');
      const diffInDays = Math.abs(inputDate.diff(targetDate, 'day'));

      if (diffInDays <= toleranceDays) {
        isValidWeek = true;
        matchedWeek = week;
        break;
      }
    }

    if (!isValidWeek) {
      const weekList = targetWeeks.join(', ');
      // Calculate the valid date range for better error messaging
      const validDateRanges = targetWeeks.map(week => {
        const targetDate = indexDate.add(week, 'week');
        const minValidDate = targetDate.subtract(toleranceDays, 'day');
        const maxValidDate = targetDate.add(toleranceDays, 'day');
        return `${minValidDate.format('DD/MM/YYYY')} to ${maxValidDate.format('DD/MM/YYYY')}`;
      }).join(' or ');
      
      const errorMessage = `Date must be approximately at week ${weekList} after index date (within ±${toleranceDays} days). Index date: ${indexDate.format('DD/MM/YYYY')}. Valid date range: ${validDateRanges}`;
      // console.error('[validationService] validateDateAfter26WeeksIndex: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateAfter26WeeksIndex: Exception -', error);
    const errorMessage = 'Unable to validate date at weeks after index date';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date at specific weeks after index date (Week 10, 14, 26, 52)
 * Date must be approximately at the specified week after index date (with ±7 days tolerance)
 */
export const validateDateAtWeeksAfterIndex = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateAtWeeksAfterIndex: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Get target weeks from fieldConfig (default: [10, 14, 26, 52])
    const targetWeeks = fieldConfig?.targetWeeks || [10, 14, 26, 52];
    const toleranceDays = fieldConfig?.toleranceDays || 7; // Default ±7 days tolerance

    // Check if date is close to any of the target weeks
    let isValidWeek = false;
    let matchedWeek = null;

    for (const week of targetWeeks) {
      const targetDate = indexDate.add(week, 'week');
      const diffInDays = Math.abs(inputDate.diff(targetDate, 'day'));

      if (diffInDays <= toleranceDays) {
        isValidWeek = true;
        matchedWeek = week;
        break;
      }
    }

    if (!isValidWeek) {
      const weekList = targetWeeks.join(', ');
      // Calculate the valid date ranges for better error messaging
      const validDateRanges = targetWeeks.map(week => {
        const targetDate = indexDate.add(week, 'week');
        const minValidDate = targetDate.subtract(toleranceDays, 'day');
        const maxValidDate = targetDate.add(toleranceDays, 'day');
        return `Week ${week}: ${minValidDate.format('DD/MM/YYYY')} to ${maxValidDate.format('DD/MM/YYYY')}`;
      }).join(' | ');
      
      const errorMessage = `Date must be approximately at week ${weekList} after index date (within ±${toleranceDays} days). Index date: ${indexDate.format('DD/MM/YYYY')}. Valid date ranges: ${validDateRanges}`;
      // console.error('[validationService] validateDateAtWeeksAfterIndex: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateAtWeeksAfterIndex: Exception -', error);
    const errorMessage = 'Unable to validate date at weeks after index date';
    return { isValid: false, message: errorMessage };
  }
};

/** Max date = index date + 52 weeks + END_DATE_TOLERANCE_DAYS (uses answers[dependCategoryKey].indexDate) */
const validateNotAfter52WeeksPostIndexFromCRFAnswers = (inputDate, answers) => {
  if (!answers) return null;
  const indexEventDate = answers['pdl_Checklist']?.['indexDate'];
  if (!indexEventDate) return null;
  const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');
  if (!indexDate.isValid()) return null;
  const maxDate = indexDate.add(52, 'week').add(END_DATE_TOLERANCE_DAYS, 'day');
  if (inputDate.isAfter(maxDate, 'day')) {
    return {
      isValid: false,
      message: `Date must not be after 52 weeks post-index date (+ ${END_DATE_TOLERANCE_DAYS} days). Latest allowed: ${maxDate.format('DD/MM/YYYY')} (index date: ${indexDate.format('DD/MM/YYYY')})`,
    };
  }
  return null;
};

export const validateDateAfterVDZDiscontinuation = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getCRFAnswersCached(context, fieldConfig, context.getVisitAnswers, true);

    const pdlAnswers = await getSubjectAnswersCached(context, {
      ...fieldConfig, "dependStepKey": "SubjectEnrollmentFormParent",
      "dependSectionKey": "pdl",
      "dependSubSectionKey": "",
      "dependCategoryKey": "pdl_Checklist",
      "dependFieldKey": "indexDate",
    }, getSubjectAnswers);

    if (!answers || !pdlAnswers) {
      return true; // If answers not available, skip validation
    }
  
    const vdzDiscontinuationAnswers = answers?.[fieldConfig?.dependCategoryKey];
    let vdzDiscontinuationDateValue;

    if (vdzDiscontinuationAnswers && typeof vdzDiscontinuationAnswers === 'object') {
      // Find keys that include 'Modifications_to_VDZ_Treatment_Plan__VDZ_discontinuation' and their value is true
      const discontinuationEntry = Object.entries(vdzDiscontinuationAnswers)
        .filter(
          ([key, val]) => key.includes('Modifications_to_VDZ_Treatment_Plan__VDZ_discontinuation') && (val === true || val === '1' || val === 1)
        )
        .reduce((acc, [key, val]) => {
          // Find the dynamic index if present
          const indexMatch = key.match(/Modifications_to_VDZ_Treatment_Plan__VDZ_discontinuation(-\d+)?/);
          if (indexMatch) {
            const indexPart = indexMatch[1] || '';
            const dateKey = `Modifications_to_VDZ_Treatment_Plan_date${indexPart}`;
            const dateValue = vdzDiscontinuationAnswers[dateKey];
            if (dateValue) {
              acc[dateKey] = dateValue;
            }
          }
          return acc;
        }, {});

      // Find the most recent date among the discontinuation dates
      const dates = Object.values(discontinuationEntry)
        .map(dateStr => dayjs(dateStr, 'DD/MM/YYYY'))
        .filter(date => date.isValid());

      if (dates.length > 0) {
        // Find the max/recent date
        const mostRecentDate = dates.reduce((latest, curr) =>
          curr.isAfter(latest) ? curr : latest
        );
        vdzDiscontinuationDateValue = mostRecentDate.format('DD/MM/YYYY');
      }
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const vdzDiscontinuationDate = dayjs(vdzDiscontinuationDateValue, 'DD/MM/YYYY');

    // Check that extracted date exists and is valid, and selected value date is after the extracted date
    if (!inputDate.isValid()) {
      const errorMessage = 'Invalid date format for the selected value';
      // console.error('[validationService] validateDateAfterVDZDiscontinuation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (!vdzDiscontinuationDate.isValid()) {
      const errorMessage = 'VDZ discontinuation date is not available. Please select VDZ discontinuation date in Section 9 first.';
      // console.error('[validationService] validateDateAfterVDZDiscontinuation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (!inputDate.isAfter(vdzDiscontinuationDate, 'day')) {
      const errorMessage = `Date must be after VDZ discontinuation date (${vdzDiscontinuationDate.format('DD/MM/YYYY')}).`;
      return { isValid: false, message: errorMessage };
    }
    const indexCap = validateNotAfter52WeeksPostIndexFromCRFAnswers(
      inputDate,
      pdlAnswers,
    );
    if (indexCap) return indexCap;
    return true;
  }
  catch (error) {
    // console.error('[validationService] validateDateAfterVDZDiscontinuation: Exception -', error);
    const errorMessage = 'Unable to validate date after VDZ discontinuation';
    return { isValid: false, message: errorMessage };
  }
}

/**
 * Validate date: After VDZ discontinuation date AND after date of initiation
 * Date must be after both VDZ discontinuation date and date of initiation (whichever is later)
 */
export const validateDateAfterVDZDiscontinuationAndInitiation = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    // Get VDZ discontinuation date from CRF answers
    const answers = await getCRFAnswersCached(context, fieldConfig, context.getVisitAnswers, true);
    const pdlAnswers = await getSubjectAnswersCached(context, {
      ...fieldConfig, "dependStepKey": "SubjectEnrollmentFormParent",
      "dependSectionKey": "pdl",
      "dependSubSectionKey": "",
      "dependCategoryKey": "pdl_Checklist",
      "dependFieldKey": "indexDate",
    }, getSubjectAnswers);
   
    if (!answers || !pdlAnswers) {
      return true; // If answers not available, skip validation
    }
  
    const vdzDiscontinuationAnswers = answers?.[fieldConfig?.dependCategoryKey];
    let vdzDiscontinuationDateValue = null;

    if (vdzDiscontinuationAnswers && typeof vdzDiscontinuationAnswers === 'object') {
      // Find keys that include 'Modifications_to_VDZ_Treatment_Plan__VDZ_discontinuation' and their value is true
      const discontinuationEntry = Object.entries(vdzDiscontinuationAnswers)
        .filter(
          ([key, val]) => key.includes('Modifications_to_VDZ_Treatment_Plan__VDZ_discontinuation') && (val === true || val === '1' || val === 1)
        )
        .reduce((acc, [key, val]) => {
          // Find the dynamic index if present
          const indexMatch = key.match(/Modifications_to_VDZ_Treatment_Plan__VDZ_discontinuation(-\d+)?/);
          if (indexMatch) {
            const indexPart = indexMatch[1] || '';
            const dateKey = `Modifications_to_VDZ_Treatment_Plan_date${indexPart}`;
            const dateValue = vdzDiscontinuationAnswers[dateKey];
            if (dateValue) {
              acc[dateKey] = dateValue;
            }
          }
          return acc;
        }, {});

      // Find the most recent date among the discontinuation dates
      const dates = Object.values(discontinuationEntry)
        .map(dateStr => dayjs(dateStr, 'DD/MM/YYYY'))
        .filter(date => date.isValid());

      if (dates.length > 0) {
        // Find the max/recent date
        const mostRecentDate = dates.reduce((latest, curr) =>
          curr.isAfter(latest) ? curr : latest
        );
        vdzDiscontinuationDateValue = mostRecentDate.format('DD/MM/YYYY');
      }
    }

    // Get date of initiation from formValues (same form)
    let dateOfInitiation = null;
    
    if (fieldConfig?.initiationDateKey) {
      // Get from formValues using initiationDateKey path
      let initiationDatePath = fieldConfig.initiationDateKey;

      // Handle dynamic index for repeated rows
      if (fieldConfig.key.includes('-')) {
        const indexKeys = fieldConfig.key.split('-');
        const indexKeyIndex = indexKeys[indexKeys.length - 1];
        initiationDatePath = initiationDatePath + '-' + indexKeyIndex;
      }

      const initiationDateKey = initiationDatePath.includes('.') ? initiationDatePath.split('.') : initiationDatePath;
      dateOfInitiation = initiationDatePath.includes('.')
        ? formValues[initiationDateKey[0]]?.[initiationDateKey[1]]
        : formValues[initiationDateKey];
    } else if (fieldConfig?.dependCategoryKey) {
      // Try to get from formValues using standard path
      const category = formValues?.[fieldConfig?.dependCategoryKey];
      if (category) {
        dateOfInitiation = category['dateOfInitiation'] || category['initiationDate'];
      }
    }

    // Validate input date
    const inputDate = dayjs(value, 'DD/MM/YYYY');
    if (!inputDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateAfterVDZDiscontinuationAndInitiation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Parse VDZ discontinuation date
    const vdzDiscontinuationDate = vdzDiscontinuationDateValue ? dayjs(vdzDiscontinuationDateValue, 'DD/MM/YYYY') : null;
    if (vdzDiscontinuationDate && !vdzDiscontinuationDate.isValid()) {
      const errorMessage = 'Invalid VDZ discontinuation date format';
      // console.error('[validationService] validateDateAfterVDZDiscontinuationAndInitiation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Parse date of initiation
    const initiationDate = dateOfInitiation ? dayjs(dateOfInitiation, 'DD/MM/YYYY') : null;
    if (initiationDate && !initiationDate.isValid()) {
      const errorMessage = 'Invalid date of initiation format';
      // console.error('[validationService] validateDateAfterVDZDiscontinuationAndInitiation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Find the later date between VDZ discontinuation date and initiation date
    let latestRequiredDate = null;
    if (vdzDiscontinuationDate && initiationDate) {
      latestRequiredDate = vdzDiscontinuationDate.isAfter(initiationDate) ? vdzDiscontinuationDate : initiationDate;
    } else if (vdzDiscontinuationDate) {
      latestRequiredDate = vdzDiscontinuationDate;
    } else if (initiationDate) {
      latestRequiredDate = initiationDate;
    }

    // Check if date is on or before the latest required date
    const isOnOrBeforeLatestDate = !inputDate.isAfter(latestRequiredDate, 'day');

    if (isOnOrBeforeLatestDate) {
      let dateDescription = '';
      if (vdzDiscontinuationDate && initiationDate) {
        dateDescription = `both VDZ discontinuation date (${vdzDiscontinuationDate.format('DD/MM/YYYY')}) and date of initiation (${initiationDate.format('DD/MM/YYYY')})`;
      } else if (vdzDiscontinuationDate) {
        dateDescription = `VDZ discontinuation date (${vdzDiscontinuationDate.format('DD/MM/YYYY')})`;
      } else {
        dateDescription = `date of initiation (${initiationDate.format('DD/MM/YYYY')})`;
      }
      const errorMessage = `Date must be after ${dateDescription}. Earliest allowed: ${latestRequiredDate.add(1, 'day').format('DD/MM/YYYY')}`;
      // console.error('[validationService] validateDateAfterVDZDiscontinuationAndInitiation: Validation failed -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    const indexCap = validateNotAfter52WeeksPostIndexFromCRFAnswers(
      inputDate,
      pdlAnswers,
    );
    
    if (indexCap) return indexCap;
    return true;
  } catch (error) {
    // console.error('[validationService] validateDateAfterVDZDiscontinuationAndInitiation: Exception -', error);
    const errorMessage = 'Unable to validate date after VDZ discontinuation and initiation';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date range: (Index date – 12 months) to index date
 * Date must be between (Index date – 12 months) and index date (inclusive)
 */
export const validateDateRangeIndexMinus12Months = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateRangeIndexMinus12Months: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Calculate (Index date – 12 months)
    const minDate = indexDate.subtract(12, 'month');
    const maxDate = indexDate;

    // Check if date is before (Index date – 12 months)
    const isBeforeMinDate = inputDate.isBefore(minDate, 'day');
    // Check if date is after index date
    const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

    if (isBeforeMinDate) {
      const errorMessage = `Date must not be before (Index date – 12 months). Earliest allowed: ${minDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }
    if (isAfterMaxDate) {
      const errorMessage = `Date must not be after index date. Latest allowed: ${maxDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateRangeIndexMinus12Months: Exception -', error);
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date: After index date AND after date of initiation
 * Date must be after both index date and date of initiation (whichever is later)
 */
export const validateDateAfterIndexAndInitiation = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];
    
    // Get date of initiation from fieldConfig
    // Can be from answers or from formValues depending on configuration
    let dateOfInitiation = null;
    
    if (fieldConfig?.initiationDateKey) {
      // Get from formValues using initiationDateKey path
      let initiationDatePath = fieldConfig.initiationDateKey;

      if (fieldConfig.key.includes('-')) {
        const indexKeys = fieldConfig.key.split('-');
        const indexKeyIndex = indexKeys[indexKeys.length - 1];
        initiationDatePath = initiationDatePath + '-' + indexKeyIndex;
      }

      const initiationDateKey = initiationDatePath.includes('.') ? initiationDatePath.split('.') : initiationDatePath;
      dateOfInitiation = initiationDatePath.includes('.')
        ? formValues[initiationDateKey[0]]?.[initiationDateKey[1]]
        : formValues[initiationDateKey];
    } else if (fieldConfig?.dependCategoryKey) {
      // Try to get from answers using standard path
      dateOfInitiation =
        answers?.[fieldConfig?.dependCategoryKey]?.['dateOfInitiation'] ||
        answers?.[fieldConfig?.dependCategoryKey]?.['initiationDate'];
    }

    if (!indexEventDate && !dateOfInitiation) {
      return true; // If both dates not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = indexEventDate ? dayjs(indexEventDate, 'DD/MM/YYYY') : null;
    const initiationDate = dateOfInitiation ? dayjs(dateOfInitiation, 'DD/MM/YYYY') : null;

    if (!inputDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateAfterIndexAndInitiation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (indexDate && !indexDate.isValid()) {
      const errorMessage = 'Invalid index date format';
      // console.error('[validationService] validateDateAfterIndexAndInitiation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    if (initiationDate && !initiationDate.isValid()) {
      const errorMessage = 'Invalid date of initiation format';
      // console.error('[validationService] validateDateAfterIndexAndInitiation: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Find the later date between index date and initiation date
    let latestRequiredDate = null;
    if (indexDate && initiationDate) {
      latestRequiredDate = indexDate.isAfter(initiationDate) ? indexDate : initiationDate;
    } else if (indexDate) {
      latestRequiredDate = indexDate;
    } else if (initiationDate) {
      latestRequiredDate = initiationDate;
    }

    if (!latestRequiredDate) {
      return true; // No reference date available
    }

    // Check if date is on or before the latest required date
    const isOnOrBeforeLatestDate = !inputDate.isAfter(latestRequiredDate, 'day');

    if (isOnOrBeforeLatestDate) {
      let dateDescription = '';
      if (indexDate && initiationDate) {
        dateDescription = 'both index date and date of initiation';
      } else if (indexDate) {
        dateDescription = 'index date';
      } else {
        dateDescription = 'date of initiation';
      }
      const errorMessage = `Date must be after ${dateDescription}. Earliest allowed: ${latestRequiredDate.add(1, 'day').format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateAfterIndexAndInitiation: Exception -', error);
    const errorMessage = 'Unable to validate date after index and initiation';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date from diagnosis date up to 52 weeks post-index date (+ 4 weeks / 28 days tolerance)
 * General validation for post-index period: (diagnosis date to 52 weeks post-index + 28 days)
 */
export const validateDateFromDiagnosisTo52WeeksPostIndex = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, {...fieldConfig, dependFieldKey: ""}, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
    const diagnosisDateInfo = getDiagnosisDate(answers, fieldConfig?.dependCategoryKey);

    if (!diagnosisDateInfo) {
      return true; // If diagnosis date not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Calculate 52 weeks post-index date; allow end date + 4 weeks (28 days)
    const endDate52Weeks = indexDate.add(52, 'week');
    const maxDate = endDate52Weeks.add(END_DATE_TOLERANCE_DAYS, 'day');
    const maxYear = maxDate.year();

    // Check if only yearOfDiagnosis is available (no exact date)
    if (diagnosisDateInfo.source === 'yearOfDiagnosis') {
      // Year-based validation: year must be >= yearOfDiagnosis AND <= maxYear (52 weeks post-index year + tolerance)
      const diagnosisYear = parseInt(diagnosisDateInfo.originalValue, 10);
      const inputYear = inputDate.year();

      if (isNaN(diagnosisYear)) {
        return true; // If diagnosis year is invalid, skip validation
      }

      const isBeforeDiagnosisYear = inputYear < diagnosisYear;
      const isAfterMaxYear = inputYear > maxYear;

      if (isBeforeDiagnosisYear || isAfterMaxYear) {
        const errorMessage = `Date year must be between Diagnosis Year (${diagnosisYear}) and 52 weeks post-index year (${maxYear}, + ${END_DATE_TOLERANCE_DAYS} days)`;
        return { isValid: false, message: errorMessage };
      }

      // If same year as max, still check the exact date constraint
      if (inputYear === maxYear && inputDate.isAfter(maxDate, 'day')) {
        const errorMessage = `Date must not be after 52 weeks post-index date (+ ${END_DATE_TOLERANCE_DAYS} days). Latest allowed: ${maxDate.format('DD/MM/YYYY')}`;
        return { isValid: false, message: errorMessage };
      }
    } else {
      // Exact date validation
      const diagnosisDate = diagnosisDateInfo.date;

      if (!diagnosisDate.isValid()) {
        const errorMessage = 'Invalid date format';
        return { isValid: false, message: errorMessage };
      }

      // Check if date is before diagnosis date
      const isBeforeDiagnosisDate = inputDate.isBefore(diagnosisDate, 'day');
      // Check if date is after 52 weeks post-index date + tolerance
      const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

      if (isBeforeDiagnosisDate) {
        const errorMessage = `Date must not be before date of diagnosis (${diagnosisDate.format('DD/MM/YYYY')}). Earliest allowed: ${diagnosisDate.format('DD/MM/YYYY')}`;
        return { isValid: false, message: errorMessage };
      }

      if (isAfterMaxDate) {
        const errorMessage = `Date must not be after 52 weeks post-index date (+ ${END_DATE_TOLERANCE_DAYS} days). Latest allowed: ${maxDate.format('DD/MM/YYYY')} (index date: ${indexDate.format('DD/MM/YYYY')}, diagnosis date: ${diagnosisDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Read a DD/MM/YYYY date from formValues by path. Supports `category.field` and dynamic row suffix from fieldConfig.key (e.g. field-1).
 * @param {Object} fieldConfig
 * @param {Object} formValues
 * @param {string} pathKey
 * @returns {import('dayjs').Dayjs | null}
 */
const getFormDateFromPath = (fieldConfig, formValues, pathKey) => {
  if (!pathKey || formValues == null) return null;
  let path = pathKey;
  if (fieldConfig?.key && String(fieldConfig.key).includes('-')) {
    const indexKeys = String(fieldConfig.key).split('-');
    const indexKeyIndex = indexKeys[indexKeys.length - 1];
    path = path + '-' + indexKeyIndex;
  }
  const pathParts = path.includes('.') ? path.split('.') : path;
  const raw = path.includes('.')
    ? formValues[pathParts[0]]?.[pathParts[1]]
    : formValues[pathParts];
  if (!raw) return null;
  const d = dayjs(raw, 'DD/MM/YYYY');
  return d.isValid() ? d : null;
};

/**
 * Validate that selected date is within the window: day after index date to 52 weeks from index date + 28 days (inclusive).
 * Index date itself is excluded. Uses fieldConfig.dependCategoryKey to read indexDate from subject answers.
 * Optional fieldConfig.startDateKey: date must be on or after that field's value (from formValues).
 * Optional fieldConfig.stopDateKey: date must be on or before that field's value (from formValues).
 */
export const validateDateWithin52WeeksPlus28DaysPostIndex = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, { ...fieldConfig, dependFieldKey: "" }, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // Day after index date to 52 weeks from index date + 4 weeks (28 days) tolerance (index date excluded)
    const minDate = indexDate.add(1, 'day');
    const endDate52Weeks = indexDate.add(52, 'week');
    const maxDate = endDate52Weeks.add(END_DATE_TOLERANCE_DAYS, 'day');

    const isBeforeMinDate = inputDate.isBefore(minDate, 'day');
    const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

    if (isBeforeMinDate) {
      const errorMessage = `Date must be after index date (${indexDate.format('DD/MM/YYYY')}). Earliest allowed: ${minDate.format('DD/MM/YYYY')}. Valid range: ${minDate.format('DD/MM/YYYY')} to ${maxDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    if (isAfterMaxDate) {
      const errorMessage = `Date must not be after 52 weeks post-index date (+ ${END_DATE_TOLERANCE_DAYS} days). Latest allowed: ${maxDate.format('DD/MM/YYYY')} (index date: ${indexDate.format('DD/MM/YYYY')}). Valid range: ${minDate.format('DD/MM/YYYY')} to ${maxDate.format('DD/MM/YYYY')}`;
      return { isValid: false, message: errorMessage };
    }

    // Optional: must be on or after start date field (from current form values)
    if (fieldConfig?.startDateKey) {
      const startDate = getFormDateFromPath(fieldConfig, formValues, fieldConfig.startDateKey);
      if (startDate && inputDate.isBefore(startDate, 'day')) {
        const errorMessage = `Date must not be before start date (${startDate.format('DD/MM/YYYY')}). Earliest allowed: ${startDate.format('DD/MM/YYYY')}`;
        return { isValid: false, message: errorMessage };
      }
    }

    // Optional: must be on or before stop date field (from current form values)
    if (fieldConfig?.stopDateKey) {
      const stopDate = getFormDateFromPath(fieldConfig, formValues, fieldConfig.stopDateKey);
      if (stopDate && inputDate.isAfter(stopDate, 'day')) {
        const errorMessage = `Date must not be after stop date (${stopDate.format('DD/MM/YYYY')}). Latest allowed: ${stopDate.format('DD/MM/YYYY')}`;
        return { isValid: false, message: errorMessage };
      }
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date range';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date of procedure: From date of diagnosis up to 52 weeks post-index date + 28 days tolerance
 */
export const validateDateOfProcedure = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, {...fieldConfig, dependFieldKey: ""}, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    // Get diagnosis date (from dateOfDiagnosis or yearOfDiagnosis)
    const diagnosisDateInfo = getDiagnosisDate(answers, fieldConfig?.dependCategoryKey);

    if (!diagnosisDateInfo) {
      return true; // If diagnosis date not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      return { isValid: false, message: errorMessage };
    }

    // 52 weeks post-index + 28 days tolerance
    const endDate52Weeks = indexDate.add(52, 'week');
    const maxDate = endDate52Weeks.add(END_DATE_TOLERANCE_DAYS, 'day');
    const maxYear = maxDate.year();

    // Check if only yearOfDiagnosis is available (no exact date)
    if (diagnosisDateInfo.source === 'yearOfDiagnosis') {
      // Year-based validation: year must be >= yearOfDiagnosis AND <= maxYear
      const diagnosisYear = parseInt(diagnosisDateInfo.originalValue, 10);
      const inputYear = inputDate.year();

      if (isNaN(diagnosisYear)) {
        return true; // If diagnosis year is invalid, skip validation
      }

      const isBeforeDiagnosisYear = inputYear < diagnosisYear;
      const isAfterMaxYear = inputYear > maxYear;

      if (isBeforeDiagnosisYear || isAfterMaxYear) {
        const errorMessage = `Date year must be between Diagnosis Year (${diagnosisYear}) and ${maxYear}`;
        return { isValid: false, message: errorMessage };
      }

      // If same year as max, still check the exact date constraint
      if (inputYear === maxYear && inputDate.isAfter(maxDate, 'day')) {
        const errorMessage = `Date must not be after ${maxDate.format('DD/MM/YYYY')} (52 weeks post-index + ${END_DATE_TOLERANCE_DAYS} days)`;
        return { isValid: false, message: errorMessage };
      }
    } else {
      // Exact date validation
      const diagnosisDate = diagnosisDateInfo.date;

      if (!diagnosisDate.isValid()) {
        const errorMessage = 'Invalid date format';
        return { isValid: false, message: errorMessage };
      }

      // Check if date is before diagnosis date
      const isBeforeDiagnosisDate = inputDate.isBefore(diagnosisDate, 'day');
      const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');

      if (isBeforeDiagnosisDate) {
        const errorMessage = `Date must not be before date of diagnosis (${diagnosisDate.format('DD/MM/YYYY')}). Earliest allowed: ${diagnosisDate.format('DD/MM/YYYY')}`;
        return { isValid: false, message: errorMessage };
      }

      if (isAfterMaxDate) {
        const maxDateDisplay = maxDate.format('DD/MM/YYYY');
        const errorMessage = `Date must not be after ${maxDateDisplay} (52 weeks post-index: ${endDate52Weeks.format('DD/MM/YYYY')} + ${END_DATE_TOLERANCE_DAYS} days). Latest allowed: ${maxDateDisplay} (index date: ${indexDate.format('DD/MM/YYYY')}, diagnosis date: ${diagnosisDate.format('DD/MM/YYYY')})`;
        return { isValid: false, message: errorMessage };
      }
    }

    return true;
  } catch (error) {
    const errorMessage = 'Unable to validate date of procedure';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Validate date of admission: From date of diagnosis up to 52 weeks post-index date
 */
export const validateDateOfAdmission = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  // Reuse the general validation logic
  return await validateDateFromDiagnosisTo52WeeksPostIndex(
    value,
    fieldConfig,
    formValues,
    context,
    getSubjectAnswers
  );
};

/**
 * Validate date of discharge: After date of admission and no later than 52 weeks post-index date
 */
export const validateDateOfDischarge = async (
  value,
  fieldConfig,
  formValues,
  context,
  getSubjectAnswers
) => {
  if (!value) {
    return true; // Empty values handled by required validation
  }

  try {
    const answers = await getSubjectAnswersCached(context, fieldConfig, getSubjectAnswers);

    if (!answers) {
      return true; // If answers not available, skip validation
    }

    const indexEventDate = answers?.[fieldConfig?.dependCategoryKey]?.['indexDate'];

    if (!indexEventDate) {
      return true; // If index date not available, skip validation
    }

    const inputDate = dayjs(value, 'DD/MM/YYYY');
    const indexDate = dayjs(indexEventDate, 'DD/MM/YYYY');

    if (!inputDate.isValid() || !indexDate.isValid()) {
      const errorMessage = 'Invalid date format';
      // console.error('[validationService] validateDateOfDischarge: Error -', errorMessage);
      return { isValid: false, message: errorMessage };
    }

    // Get date of admission from formValues (same form) or answers
    let dateOfAdmission = null;

    // Try to get from formValues first (same form/category)
    if (fieldConfig?.admissionDateKey) {
      let admissionDatePath = fieldConfig.admissionDateKey;

      // Handle repeated rows
      if (fieldConfig.key.includes('-')) {
        const indexKeys = fieldConfig.key.split('-');
        const indexKeyIndex = indexKeys[indexKeys.length - 1];
        admissionDatePath = admissionDatePath + '-' + indexKeyIndex;
      }

      const admissionDateKey = admissionDatePath.includes('.') ? admissionDatePath.split('.') : admissionDatePath;
      dateOfAdmission = admissionDatePath.includes('.')
        ? formValues[admissionDateKey[0]]?.[admissionDateKey[1]]
        : formValues[admissionDateKey];
    } else if (fieldConfig?.dependCategoryKey) {
      // Try to get from same category in formValues
      const category = formValues?.[fieldConfig?.dependCategoryKey];
      if (category) {
        dateOfAdmission = category['dateOfAdmission'] || category['admissionDate'];
      }

      // If not in formValues, try answers
      if (!dateOfAdmission) {
        const categoryAnswers = answers?.[fieldConfig?.dependCategoryKey];
        if (categoryAnswers) {
          dateOfAdmission = categoryAnswers['dateOfAdmission'] || categoryAnswers['admissionDate'];
        }
      }
    }

    // Calculate 52 weeks post-index date + 4 weeks (28 days) tolerance
    const endDate52Weeks = indexDate.add(52, 'week');
    const maxDate = endDate52Weeks.add(END_DATE_TOLERANCE_DAYS, 'day');

    // Validate against date of admission (if available)
    if (dateOfAdmission) {
      const admissionDate = dayjs(dateOfAdmission, 'DD/MM/YYYY');

      if (!admissionDate.isValid()) {
        const errorMessage = 'Invalid date of admission format';
        // console.error('[validationService] validateDateOfDischarge: Error -', errorMessage);
        return { isValid: false, message: errorMessage };
      }

      // Check if date is on or before date of admission
      const isOnOrBeforeAdmission = !inputDate.isAfter(admissionDate, 'day');
      if (isOnOrBeforeAdmission) {
        const errorMessage = `Date must be after date of admission (${admissionDate.format('DD/MM/YYYY')}). Earliest allowed: ${admissionDate.add(1, 'day').format('DD/MM/YYYY')}`;
        return { isValid: false, message: errorMessage };
      }
    }

    // Check if date is after 52 weeks post-index date + 28 days
    const isAfterMaxDate = inputDate.isAfter(maxDate, 'day');
    if (isAfterMaxDate) {
      const errorMessage = `Date must not be after 52 weeks post-index date (+ ${END_DATE_TOLERANCE_DAYS} days). Latest allowed: ${maxDate.format('DD/MM/YYYY')} (index date: ${indexDate.format('DD/MM/YYYY')})`;
      return { isValid: false, message: errorMessage };
    }

    return true;
  } catch (error) {
    // console.error('[validationService] validateDateOfDischarge: Exception -', error);
    const errorMessage = 'Unable to validate date of discharge';
    return { isValid: false, message: errorMessage };
  }
};

/**
 * Check if new validation system is enabled via feature flag
 */
export const isNewValidationEnabled = () => {
    return true
//   return process.env.REACT_APP_USE_NEW_VALIDATION === 'true' || false;
};

